import{useEffect}from"react";
import{useAppI18n}from"../lib/appI18n";
import{
 securitySurfaceBase,
 translateSecuritySurface,
 type SecuritySurfaceLocale,
}from"../lib/securitySurfaceMessages";

const ATTRS=["aria-label","title","placeholder","alt"] as const;
const IGNORE=new Set(["SCRIPT","STYLE","CODE","PRE","TEXTAREA"]);
const originalText=new WeakMap<Text,string>();
const originalAttrs=new WeakMap<Element,Map<string,string>>();

function plural(locale:SecuritySurfaceLocale,n:number,en:string,tr:string,deOne:string,deMany:string){
 if(locale==="tr")return `${n} ${tr}`;
 if(locale==="de")return `${n} ${n===1?deOne:deMany}`;
 return `${n} ${en}${n===1?"":"s"}`;
}

function dynamicTranslate(value:string,locale:SecuritySurfaceLocale):string{
 const exact=translateSecuritySurface(value,locale);
 if(exact!==value||locale==="en")return exact;

 let m=value.match(/^(\d+)\s+requests?$/i);
 if(m)return plural(locale,Number(m[1]),"request","istek","Anfrage","Anfragen");

 m=value.match(/^(\d+)\s+temporary grants?$/i);
 if(m)return plural(locale,Number(m[1]),"temporary grant","geçici yetki","temporäre Freigabe","temporäre Freigaben");

 m=value.match(/^Approve privileged access request for (.+)$/);
 if(m){
  if(locale==="tr")return `${m[1]} için ayrıcalıklı erişim isteğini onayla`;
  if(locale==="de")return `Privilegierte Zugriffsanfrage für ${m[1]} genehmigen`;
 }

 m=value.match(/^Deny privileged access request for (.+)$/);
 if(m){
  if(locale==="tr")return `${m[1]} için ayrıcalıklı erişim isteğini reddet`;
  if(locale==="de")return `Privilegierte Zugriffsanfrage für ${m[1]} ablehnen`;
 }

 m=value.match(/^Effective access for (.+)$/);
 if(m){
  if(locale==="tr")return `${m[1]} için etkin yetkiler`;
  if(locale==="de")return `Effektive Rechte für ${m[1]}`;
 }

 m=value.match(/^Authentication methods for (.+)$/);
 if(m){
  if(locale==="tr")return `${m[1]} için kimlik doğrulama yöntemleri`;
  if(locale==="de")return `Authentifizierungsmethoden für ${m[1]}`;
 }

 m=value.match(/^Source\s*·\s*(.+)$/);
 if(m){
  if(locale==="tr")return `Kaynak · ${m[1]}`;
  if(locale==="de")return `Quelle · ${m[1]}`;
 }

 m=value.match(/^(.+)\s+need review$/);
 if(m){
  if(locale==="tr")return `${m[1]} inceleme gerektiriyor`;
  if(locale==="de")return `${m[1]} müssen geprüft werden`;
 }

 m=value.match(/^(.+)\s+below minimum$/);
 if(m){
  if(locale==="tr")return `${m[1]} minimum değerin altında`;
  if(locale==="de")return `${m[1]} unter dem Mindestwert`;
 }

 m=value.match(/^(\d+)\s+passkeys?$/i);
 if(m){
  if(locale==="tr")return `${m[1]} passkey`;
  if(locale==="de")return `${m[1]} Passkey${Number(m[1])===1?"":"s"}`;
 }
 m=value.match(/^(\d+)\s+synced passkeys?\s*·\s*(.+)$/i);
 if(m){
  if(locale==="tr")return `${m[1]} senkronize passkey · Desteklenen cihazlarda kullanılabilir.`;
  if(locale==="de")return `${m[1]} synchronisierte Passkey${Number(m[1])===1?"":"s"} · Auf unterstützten Geräten verfügbar.`;
 }
 m=value.match(/^(\d+)\s+independent recovery methods? available\.$/i);
 if(m){
  if(locale==="tr")return `${m[1]} bağımsız kurtarma yöntemi kullanılabilir.`;
  if(locale==="de")return `${m[1]} unabhängige Wiederherstellungsmethode${Number(m[1])===1?"":"n"} verfügbar.`;
 }
 m=value.match(/^(\d+)\s+items?$/i);
 if(m){
  if(locale==="tr")return `${m[1]} kayıt`;
  if(locale==="de")return `${m[1]} Eintrag${Number(m[1])===1?"":"e"}`;
 }
 m=value.match(/^(\d+)\s+vault security signals? need review$/i);
 if(m){
  if(locale==="tr")return `${m[1]} kasa güvenlik sinyali incelenmeli`;
  if(locale==="de")return `${m[1]} Tresor-Sicherheitssignal${Number(m[1])===1?"":"e"} müssen geprüft werden`;
 }

 return value;
}

function translateWithWhitespace(value:string,locale:SecuritySurfaceLocale):string{
 const m=value.match(/^(\s*)(.*?)(\s*)$/s);
 if(!m)return value;
 return m[1]+dynamicTranslate(securitySurfaceBase(m[2]),locale)+m[3];
}

function translateTextNode(node:Text,locale:SecuritySurfaceLocale){
 const parent=node.parentElement;
 if(!parent||IGNORE.has(parent.tagName))return;
 const current=node.nodeValue??"";
 let base=originalText.get(node);
 if(base===undefined){
  base=securitySurfaceBase(current);
  originalText.set(node,base);
 }else{
  const expected=translateWithWhitespace(base,locale);
  if(current!==expected){
   const normalized=securitySurfaceBase(current);
   if(normalized!==securitySurfaceBase(expected)){
    base=normalized;
    originalText.set(node,base);
   }
  }
 }
 const next=translateWithWhitespace(base,locale);
 if(current!==next)node.nodeValue=next;
}

function translateElementAttrs(el:Element,locale:SecuritySurfaceLocale){
 let saved=originalAttrs.get(el);
 if(!saved){saved=new Map();originalAttrs.set(el,saved)}
 for(const attr of ATTRS){
  const current=el.getAttribute(attr);
  if(current==null)continue;
  let base=saved.get(attr);
  if(base===undefined){
   base=securitySurfaceBase(current);
   saved.set(attr,base);
  }else{
   const expected=dynamicTranslate(base,locale);
   if(current!==expected){
    const normalized=securitySurfaceBase(current);
    if(normalized!==securitySurfaceBase(expected)){
     base=normalized;
     saved.set(attr,base);
    }
   }
  }
  const next=dynamicTranslate(base,locale);
  if(current!==next)el.setAttribute(attr,next);
 }
}

function apply(root:Element,locale:SecuritySurfaceLocale){
 translateElementAttrs(root,locale);
 const walker=document.createTreeWalker(root,NodeFilter.SHOW_ELEMENT|NodeFilter.SHOW_TEXT);
 let node:Node|null=walker.currentNode;
 while(node){
  if(node.nodeType===Node.TEXT_NODE)translateTextNode(node as Text,locale);
  else if(node.nodeType===Node.ELEMENT_NODE)translateElementAttrs(node as Element,locale);
  node=walker.nextNode();
 }
}

export function SecuritySurfaceI18n(){
 const{locale}=useAppI18n();
 useEffect(()=>{
  const root=document.querySelector(".v2-app-shell")??document.querySelector(".app-shell");
  if(!root)return;
  let applying=false;
  const run=()=>{
   if(applying)return;
   applying=true;
   try{
    apply(root,locale);
    document.documentElement.lang=locale;
   }finally{applying=false}
  };
  run();
  const observer=new MutationObserver(mutations=>{
   if(applying)return;
   applying=true;
   try{
    for(const mutation of mutations){
     if(mutation.type==="characterData"&&mutation.target.nodeType===Node.TEXT_NODE){
      translateTextNode(mutation.target as Text,locale);
     }
     if(mutation.type==="attributes"&&mutation.target.nodeType===Node.ELEMENT_NODE){
      translateElementAttrs(mutation.target as Element,locale);
     }
     for(const added of mutation.addedNodes){
      if(added.nodeType===Node.TEXT_NODE)translateTextNode(added as Text,locale);
      else if(added.nodeType===Node.ELEMENT_NODE)apply(added as Element,locale);
     }
    }
   }finally{applying=false}
  });
  observer.observe(root,{subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:[...ATTRS]});
  return()=>observer.disconnect();
 },[locale]);
 return null;
}
